import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  FiHome,
  FiInbox,
  FiFileText,
  FiShoppingCart,
  FiDollarSign,
  FiList,
  FiSettings,
  FiBox,
  FiLayers,
  FiUsers,
  FiBarChart,
  FiChevronDown,
  FiClipboard,
} from 'react-icons/fi';
import { useAuth } from '../context/AuthContext';
import { hasScreenPermission } from '../lib/permissions';
// The Review badge's two halves — a catalogue-addition count with no endpoint
// of its own (`limit: 1`, read `total`) and the packing-alerts total already
// read the same way by its own list screen.
import { countPendingReviews, REVIEW_QUEUE_CHANGED_EVENT } from '../lib/catalogueSubmissionsApi';
import { listPackingAlerts } from '../lib/packingAlertsApi';
import SidebarTrail from '../features/rfqLifecycle/SidebarTrail';
import { useItemTrail } from '../features/rfqLifecycle/useItemTrail';
import { trailTargetFromLocation } from '../features/rfqLifecycle/trailTarget';
import { STAGE_SPECS, type TrailBox } from '../features/rfqLifecycle/lifecycleModel';
// BUG_REPORTER:BEGIN — temporary feature, remove per docs/BUG_REPORTER.md
import { BugReporter } from '../features/bugReport/BugReporter';
// BUG_REPORTER:END

/* ----------------------------------------------------------
   CUSTOM ICONS (Feather-ish inline SVG)
---------------------------------------------------------- */
const ArrowLeftIcon = ({ size = 16, className = '' }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    className={className}
  >
    <polyline points="15 18 9 12 15 6" />
  </svg>
);

const ArrowRightIcon = ({ size = 16, className = '' }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    className={className}
  >
    <polyline points="9 18 15 12 9 6" />
  </svg>
);

// Vendors icon
const TruckIcon = ({
  size = 18,
  className = '',
}: {
  size?: number;
  className?: string;
}) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    className={className}
  >
    <rect x="1" y="3" width="15" height="13" />
    <polygon points="16 8 20 8 23 11 23 16 16 16 16 8" />
    <circle cx="5.5" cy="18.5" r="1.5" />
    <circle cx="18.5" cy="18.5" r="1.5" />
  </svg>
);

// Confirmed Order icon
const CheckBadgeIcon = ({
  size = 18,
  className = '',
}: {
  size?: number;
  className?: string;
}) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    className={className}
  >
    <circle cx="12" cy="12" r="9" />
    <path d="M8.5 12.5l2.2 2.2 4.8-5.2" />
  </svg>
);

/* ----------------------------------------------------------
   MENU CONFIGURATION
---------------------------------------------------------- */
const menu = [
  { key: 'home', label: 'Home', icon: FiHome, permissionScreen: 'dashboard', path: '/app' },
  { key: 'rfq-inbox', label: 'Inbox', icon: FiInbox, permissionScreen: 'quotations', path: '/rfq' },
  { key: 'quote-generator', label: 'Quote Generator', icon: FiFileText, permissionScreen: 'quotations', path: '/quotations' },
  { key: 'confirmed-order', label: 'Confirmed Order', icon: CheckBadgeIcon, permissionScreen: 'confirmed_orders', path: '/confirmed-orders' },
  { key: 'vendor-purchase-order', label: 'Vendor Purchase Order', icon: FiShoppingCart, permissionScreen: 'vendor_purchase_orders', path: '/vendor-purchase-orders' },

  { key: 'invoice', label: 'Client Invoices', icon: FiDollarSign, permissionScreen: 'invoices', path: '/client-invoices' },
  { key: 'credit-notes', label: 'Credit Notes', icon: FiFileText, permissionScreen: 'invoices', path: '/credit-notes' },

  // Vendor Invoices
  {
    key: 'vendor-invoices',
    label: 'Vendor Invoices',
    icon: FiDollarSign,
    permissionScreen: 'invoices',
    path: '/vendor-invoices',
  },

  { key: 'client-price-list', label: 'Client Price List', icon: FiList, permissionScreen: 'products', path: '/client-price-lists' },
  { key: 'master-catalog', label: 'Master Catalog', icon: FiLayers, permissionScreen: 'products', path: '/master-catalogue' },
  // Daily work (a queue to clear), not configuration — sits with Master Catalog, not under Admin Settings.
  { key: 'review', label: 'Review', icon: FiClipboard, permissionScreen: 'catalogue_review', path: '/review' },
  { key: 'inventory', label: 'Ship Chandlery Inventory', icon: FiBox, permissionScreen: 'inventory', path: '/inventory' },
  { key: 'inventory-pantry', label: 'Pantry Inventory', icon: FiBox, permissionScreen: 'inventory', path: '/inventory/pantry' },
  { key: 'accounts', label: 'Accounts', icon: FiDollarSign, permissionScreen: 'financials', path: '/accounts' },
  { key: 'reports', label: 'Reports', icon: FiBarChart, permissionScreen: 'reports', path: '/reports' },

  // Admin
  { key: 'users', label: 'User Management', icon: FiUsers, permissionScreen: 'users', path: '/users' },
  { key: 'vendors', label: 'Vendor List', icon: TruckIcon, permissionScreen: 'vendors', path: '/vendors' },

  // ✅ Client List (Admin Settings -> below Vendor List)
  { key: 'clients', label: 'Client List', icon: FiUsers, permissionScreen: 'clients', path: '/clients' },

  { key: 'settings', label: 'Settings', icon: FiSettings, permissionScreen: 'settings', path: '/settings' },
];

interface SidebarProps {
  activeKey?: string;
  onChange?: (key: string) => void;
}

/**
 * A phone-width screen (below Tailwind's `md`). The 240px menu left about 150px for the page at
 * 390px wide, so it starts as the 64px icon rail there; the button still opens it.
 */
const isPhoneWidth = () =>
  typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(max-width: 767px)').matches;

export const Sidebar: React.FC<SidebarProps> = ({ activeKey, onChange }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(isPhoneWidth);
  const [adminOpen, setAdminOpen] = useState(true);

  const visibleMenuItems = useMemo(
    () => menu.filter((item) => hasScreenPermission(user, item.permissionScreen, 'view')),
    [user]
  );

  /**
   * The Review badge — pending catalogue additions plus pending packing
   * alerts. Fetched once on mount (only when the item is even visible, so a
   * user without the permission never issues a request the server would
   * refuse) and refreshed on `REVIEW_QUEUE_CHANGED_EVENT`, dispatched by the
   * Review page after a decision — NOT on an interval; nothing here polls.
   */
  const canSeeReview = useMemo(
    () => hasScreenPermission(user, 'catalogue_review', 'view'),
    [user]
  );
  const canSeeQuotations = useMemo(() => hasScreenPermission(user, 'quotations', 'view'), [user]);
  const [reviewCount, setReviewCount] = useState(0);
  useEffect(() => {
    if (!canSeeReview) {
      setReviewCount(0);
      return;
    }
    let alive = true;
    const refresh = () => {
      Promise.all([
        countPendingReviews().catch(() => 0),
        listPackingAlerts({ limit: 1 })
          .then((res) => res.meta?.total ?? 0)
          .catch(() => 0),
      ]).then(([additions, packing]) => {
        if (alive) setReviewCount(additions + packing);
      });
    };
    refresh();
    window.addEventListener(REVIEW_QUEUE_CHANGED_EVENT, refresh);
    return () => {
      alive = false;
      window.removeEventListener(REVIEW_QUEUE_CHANGED_EVENT, refresh);
    };
  }, [canSeeReview]);

  /**
   * Screens with no nav entry of their own still belong to a section.
   *
   * Item Groups is opened from the Master Catalogue, and Currency Conversion is
   * the Exchange Rate tab of Settings under another URL. Until 2026-09-10 both
   * fell through to the "home" default below, so somebody deep in Item Groups
   * was told by the sidebar that they were on the dashboard.
   */
  const SECTION_OF_PATH: Record<string, string> = {
    '/item-groups': 'master-catalog',
    '/currency-conversion': 'settings',
  };

  const getCurrentKey = () => {
    if (activeKey) return activeKey;
    const exact = menu.find((i) => i.path === location.pathname);
    if (exact) return exact.key;
    const prefix = menu.find((i) => location.pathname.startsWith(i.path + '/'));
    if (prefix) return prefix.key;
    if (SECTION_OF_PATH[location.pathname]) return SECTION_OF_PATH[location.pathname];
    // Nothing highlighted, rather than Home highlighted. The vendor Price List
    // and the bug reports have no section, and claiming they are the dashboard
    // is worse than claiming nothing.
    return location.pathname === '/app' ? 'home' : undefined;
  };

  const currentActiveKey = getCurrentKey();

  const handleItemClick = (item: typeof menu[0]) => {
    onChange?.(item.key);
    navigate(item.path);
  };

  const toggleCollapsed = () => setCollapsed((prev) => !prev);

  // The menu entries the open RFQ's cycle stands in for (Payment has none: it is not a module).
  const cycleKeys = new Set([
    'home',
    'rfq-inbox',
    'quote-generator',
    'confirmed-order',
    'vendor-purchase-order',
    'invoice',
    'credit-notes',
    'vendor-invoices',
  ]);

  const mainKeys = new Set([
    'home',
    'rfq-inbox',
    'quote-generator',
    'confirmed-order',
    'vendor-purchase-order',
    'invoice',
    'credit-notes',
    'vendor-invoices',
    'client-price-list',
    'master-catalog',
    'review',
    'inventory',
    'inventory-pantry',
    'accounts',
    'reports',
  ]);

  // ✅ Add "clients" under Admin Settings
  const adminKeys = new Set(['users', 'vendors', 'clients', 'settings']);

  const mainItems = visibleMenuItems.filter((i) => mainKeys.has(i.key));
  const adminItems = visibleMenuItems.filter((i) => adminKeys.has(i.key));

  // On a document's own page the menu's first part (Home to Vendor Invoices) gives way to that RFQ's cycle, drawn once;
  // the rest of the menu stays exactly as it was. The icon rail has no room for it, and a failed or empty read
  // leaves the plain menu.
  const target = trailTargetFromLocation(location.pathname, location.search);
  const trail = useItemTrail(target, canSeeQuotations);
  const showTrail = !!trail && !!target && !collapsed;
  const canOpenStep = (box: TrailBox) => {
    const spec = STAGE_SPECS.find((s) => s.key === box.key);
    return !!spec && hasScreenPermission(user, spec.linkScreen, 'view');
  };
  const shownMainItems = showTrail ? mainItems.filter((i) => !cycleKeys.has(i.key)) : mainItems;

  return (
    <aside
      className={[
        'bg-white border-r border-slate-200 flex flex-col no-print',
        collapsed ? 'w-16' : 'w-60',
        'min-w-0 overflow-x-hidden',
      ].join(' ')}
    >
      <div className="flex items-center justify-between px-3 py-3 border-b border-slate-200 min-w-0">
        {!collapsed && (
          <div className="text-sm font-semibold text-slate-700 truncate min-w-0">
            United Ship Chandlers
          </div>
        )}
        <button
          onClick={toggleCollapsed}
          className="text-slate-600 hover:text-slate-900 shrink-0"
          aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          {collapsed ? <ArrowRightIcon /> : <ArrowLeftIcon />}
        </button>
      </div>

      {/* The cycle is inside the scrolling nav, so it and the rest of the menu scroll together on a short screen. */}
      <nav className={['flex-1 space-y-1 min-w-0 overflow-x-hidden', showTrail ? 'pb-3' : 'py-3'].join(' ')}>
        {showTrail && trail && target ? (
          <>
            <SidebarTrail trail={trail} current={target} canOpen={canOpenStep} onOpen={(to) => navigate(to)} onHome={() => navigate('/app')} />
            <div role="separator" className="!mt-0 mb-2 border-t border-slate-200" />
          </>
        ) : null}
        {shownMainItems.map((item) => {
          const Icon: any = item.icon;
          const isActive = currentActiveKey === item.key;

          return (
            <button
              key={item.key}
              onClick={() => handleItemClick(item)}
              className={[
                'w-full flex items-center gap-3 px-3 py-2 rounded-md transition text-sm text-slate-700',
                'hover:bg-slate-50',
                'min-w-0',
                collapsed ? 'justify-center' : '',
                isActive ? 'bg-primary-50 border-l-4 border-primary-500 pl-2' : '',
              ].join(' ')}
              title={collapsed ? item.label : undefined}
            >
              <Icon size={18} className="shrink-0" />
              {!collapsed && (
                <span className="flex-1 text-left truncate whitespace-nowrap min-w-0">
                  {item.label}
                </span>
              )}
              {item.key === 'review' && reviewCount > 0 && !collapsed && (
                <span
                  className="shrink-0 rounded-full bg-rose-600 px-1.5 py-0.5 text-xs font-semibold text-white"
                  aria-label={`${reviewCount} pending in Review`}
                >
                  {reviewCount}
                </span>
              )}
            </button>
          );
        })}

        {adminItems.length > 0 && (
          <div className="mt-4 min-w-0">
            <button
              onClick={() => setAdminOpen((p) => !p)}
              className={[
                'w-full flex items-center justify-between px-3 py-2 text-sm text-slate-600',
                'hover:bg-slate-50 rounded-md',
                'min-w-0',
                collapsed ? 'justify-center' : '',
              ].join(' ')}
            >
              {!collapsed && <span className="truncate min-w-0">Admin Settings</span>}
              <FiChevronDown className={adminOpen ? 'rotate-180' : ''} />
            </button>

            {adminOpen &&
              adminItems.map((item) => {
                const Icon: any = item.icon;
                const isActive = currentActiveKey === item.key;

                return (
                  <button
                    key={item.key}
                    onClick={() => handleItemClick(item)}
                    className={[
                      'w-full flex items-center gap-3 py-2 rounded-md transition text-sm text-slate-700',
                      'hover:bg-slate-50',
                      'min-w-0',
                      collapsed ? 'justify-center px-3' : 'px-6',
                      isActive ? 'bg-primary-50 border-l-4 border-primary-500 pl-5' : '',
                    ].join(' ')}
                    title={collapsed ? item.label : undefined}
                  >
                    <Icon size={16} className="shrink-0" />
                    {!collapsed && (
                      <span className="flex-1 text-left truncate whitespace-nowrap min-w-0">
                        {item.label}
                      </span>
                    )}
                  </button>
                );
              })}
          </div>
        )}
      </nav>

      {/* BUG_REPORTER:BEGIN — temporary feature, remove per docs/BUG_REPORTER.md */}
      {/* Renders NOTHING here: it portals itself to <body> as a floating
          launcher, because a fullScreen Modal covers this whole aside and the
          button has to be reachable from on top of one. Mounted here only
          because this component renders on every authenticated screen. NOT
          permission-gated, unlike the rest of this menu: every authenticated
          user may report a bug, and there is no role for which that should
          403. */}
      <BugReporter />
      {/* BUG_REPORTER:END */}
    </aside>
  );
};
